'use strict';
import { ChangeDetectionStrategy, Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { IdynamicFormItem } from '@ispm/dynamic-form/interfaces/IdynamicFormItem';
import { IWidgetItem } from '@ispm/widgets/interfaces/widget-item.interface';
import { get, has } from 'lodash';
import { Widget } from '../../decorator/widget.decorator';
import { DynamicFormItem } from '@ispm/shared/dynamic-form/classes/dynamic-form-item.class';
import { ITEMS_TYPES } from '@ispm/shared/dynamic-form/constants/ItemsTypes';
import { RelatedEntitiesRevision } from './interfaces/related-entities-revision-interface';
import { debounceTime, map, merge, takeUntil } from 'rxjs/operators';
import { Subject, Observable } from 'rxjs';
import { FormGroup } from '@angular/forms';
import { CurationSettings } from '../../../../app/curation/components/curation-dashboard/curation-setting';
import { of } from 'rxjs/observable/of';
import {FormReflect} from "@ispm/dynamic-form/classes/form-reflect.class";

@Component({
  selector: 'ispm-related-entities',
  templateUrl: './related-entities.component.html',
  styleUrls: ['./related-entities.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})

@Widget('RelatedEntities')
export class RelatedEntitiesComponent implements OnInit, IWidgetItem, OnDestroy {
  private _widgetConfig: IdynamicFormItem;
  private showOnlyLatestRevision: boolean = true;
  private latestRevisionCode: string;
  private subject: Subject<any> = new Subject();
  public widgetDisabledStatus: boolean;
  private formItem: DynamicFormItem;
  private revisionDropdownItem: DynamicFormItem;
  private entityName: string;
  public data: { };
  public formConfig: any[];
  public revisionData: RelatedEntitiesRevision[];
  public olderRevisionSelected: boolean = false;
  public widgetConfigPayload: any;
  private _widgetFormInstanceSubmitted: boolean;
  private formBuild: boolean = false;

  constructor(private _changeDetector: ChangeDetectorRef) { }
  public get widgetFormInstanceSubmitted() {
    return this._widgetFormInstanceSubmitted;
  }

  public set widgetFormInstanceSubmitted(widgetFormInstanceSubmitted: boolean) {
    this._widgetFormInstanceSubmitted = widgetFormInstanceSubmitted;
    this._changeDetector.markForCheck();
    this._changeDetector.detectChanges();
  }

  public set widgetFormInstance(v: FormGroup) {
    if (this.parentForm) {
      this._destroyer.next();
    }
    this._widgetFormInstance = v;

    // Every time the form is passed to this component, the parent form should be updated with the latest (current) value
    this.parentForm = of(this._widgetFormInstance.getRawValue()).pipe(
      merge(this._widgetFormInstance.valueChanges),
      // The value of valueChanges is ignored because it returns only the changed fields and not the full form object
      takeUntil(this._destroyer.asObservable()),
      map(v => this._widgetFormInstance.getRawValue())
    );
  }

  public get widgetFormInstance() {
    return this._widgetFormInstance;
  }
  public widgetUpdateValueCallback: (value) => void;
  public addWidgetFormControl: (config: IdynamicFormItem) => void;
  public archived: boolean = false;
  public parentForm: Observable<any>;

  private _widgetFormInstance: FormGroup;
  private _destroyer: Subject<any> = new Subject();

  ngOnInit(): void {
    // this.parentForm = of(this.widgetFormInstance.getRawValue()).pipe(
    //   merge(this.widgetFormInstance.valueChanges),
    //   map(v=> this.widgetFormInstance.getRawValue()),
    //   tap(d => console.log(JSON.stringify(d)))
    //   );
    this.subject
    .pipe(debounceTime(400))
    .subscribe((form) => {
      if (!form || !form.data) {
        return;
      }
      if (!this.data) {
        this.data = { };
      }
      let itemDataChaged: boolean = false;
      if (form.data[this._widgetConfig.name]) {
        itemDataChaged = this.data['code'] != form.data[this.entityName]['code'];
        this.data = Object.assign(this.data, form.data[this.entityName]);
      }
      // The follwoing should be done after coping 'form.data[this.entityName]' (the line above)
      // Update revision data ONLY if revision was changed (resolves GEN-35352). Without this, when selecting revision
      // and then switching item (e.g. regimen), the new selected item will have the old item revision.
      let formChanged: boolean = false;
      if (form.updateFieldKeys.includes('revision') && form.data['revision']) {
        formChanged = true;
        this.data['revisionId'] = form.data['revision']['code'];
        this.data['revisionName'] = form.data['revision']['display'];
        this.data['curationDataId'] = form.data['revision']['curationDataId'];
        if (form.data['revision']['itemName']){
          if (this.data['display'] != form.data['revision']['itemName']){
            this.data['display'] = form.data['revision']['itemName']; //Handle the case when the related entity name itself was changed in the other selected revision (e.g. TP1 was changed to TP2 in the other revision)
            this.data = { ...this.data };//this forces the update when entity name was changed
          }
        }
        this.olderRevisionSelected = this.data['revisionId'] != this.latestRevisionCode;
        if (!this.olderRevisionSelected) {
          if (this.revisionDropdownItem) {
            this.revisionDropdownItem.setCssClass('hidden-form-item');
          }
        }
      }
      else {
        // Hide revision dropdown if another item was selected
        if (itemDataChaged) {
          formChanged = true;
          this.olderRevisionSelected = false;
          if (this.revisionDropdownItem) {
            this.revisionDropdownItem.setCssClass('hidden-form-item');
          }
        }
      }
      if (formChanged) {
        this.formConfig = [...this.formConfig];
      }
      this.widgetUpdateValueCallback.apply(this, [this.data]);
      }
    );
  }

  set widgetConfig(widgetConfig: IdynamicFormItem) {
    this._widgetConfig = widgetConfig;
    this.entityName = this._widgetConfig.name;
    const widgetPayload = get(widgetConfig, 'widgetPayload', { });
    Object.assign(this, widgetPayload);
    this.revisionData = widgetPayload.additionalSearchValues; // revision list is passed via additionalSearchValues
    this.showOnlyLatestRevision = widgetConfig.widgetPayload['showOnlyLatestRevision'];
    this.widgetConfigPayload = widgetPayload;

    // Building dynamic form component according to widget config
    this.formItem = DynamicFormItem.builder(widgetPayload.label || ' ', this.entityName, widgetPayload.type.toUpperCase())
                    .setPlaceholder(widgetPayload.placeholder).setRequired(widgetPayload.required).setAttributes(widgetPayload.attributes);
    this.formItem.options = widgetPayload.options;
    this.formItem.reflects = widgetPayload.reflects;

    let wasUndefined = this.formConfig === undefined;
    this.formConfig = [this.formItem];
    if(!wasUndefined && this.revisionDropdownItem){
      this.formConfig.push(this.revisionDropdownItem);
      this.formConfig = [...this.formConfig];
    }
    this.addWidgetFormControl.apply(this, [this.formConfig[0]]);
  }

  set widgetData(widgetData: any) {
    this.data = widgetData;
    this.buildForm();
    this.formItem.setDefault(this.data);
    if (this.revisionDropdownItem) {
      this.revisionDropdownItem.setDefault({code: this.data['revisionId'], display: this.data['revisionName']});
    }

  }

  ngOnDestroy(): void {
    this._destroyer.next();
    this._destroyer.complete();
  }
  buildForm() {
    if (this.formBuild){ //buildForm is being called on every update (becauseof this.data = { ...this.data }; )
      return;
    }
    this.formBuild = true;

    this.removePreviousDynamicFormItem('revision');
    if (this.revisionData) {
      // The widget gets an array of all revisions (not only of those who related to the specific related entity of this widget)
      // and look for the relevant one
      const currentRevisionItem: RelatedEntitiesRevision = this.revisionData.find(revision =>
        revision['revisedCurationItemId'] == this.data['code']);

      if (currentRevisionItem) {
        this.archived = currentRevisionItem.revisions.length > 0 && currentRevisionItem.revisions[0].code === CurationSettings.CURATION_STATUS_ARCHIVED;
        if (!this.archived) {
          const currentRevision = { code: this.data['revisionId'], display: this.data['revisionName']};
          this.latestRevisionCode = currentRevisionItem['revisions'][currentRevisionItem['revisions'].length - 1]['code'];
          // Showing revision dropdown and warning text ONLY when not latest revision is the current
          this.olderRevisionSelected = currentRevision['code'] != this.latestRevisionCode;
          if (this.olderRevisionSelected) {
            if (this.showOnlyLatestRevision) { // Showing only current and last revision
              for (let i = currentRevisionItem['revisions'].length - 1; i >= 0 ; i--) {
                if (currentRevisionItem['revisions'][i].code != currentRevision.code && i < currentRevisionItem['revisions'].length - 1) {
                  currentRevisionItem['revisions'].splice(i, 1);
                }
              }
            }
            const cssClass = get(this.widgetConfigPayload, 'cssClass','');
            const dropdownLabel = get(this.widgetConfigPayload, 'dropdownLabel','');
            this.revisionDropdownItem = DynamicFormItem.builder(dropdownLabel, 'revision', ITEMS_TYPES.DROPDOWN)
              .setOptions(currentRevisionItem['revisions'])
              .setCssClass(cssClass + ' related-entity-auto-complete')
              .setDefault(currentRevision);
            const reflects = this.widgetConfigPayload.revisionItemReflects;
            if (reflects) {
              this.revisionDropdownItem.addReflects(reflects);
            }

            this.formConfig.push(this.revisionDropdownItem);
            this.formConfig = [...this.formConfig];
          }
        }
        else {
          this.formItem.setCssClass('entity-archived');
          this.formItem.tooltip = 'Archived';
        }
      }
    }
  }

  // Removing DF item if exists
  removePreviousDynamicFormItem(itemName: string) {
    for (let i = this.formConfig.length - 1; i >= 0 ; i--) { // remove empty values from the object
      if (this.formConfig[i]['name'] === itemName) {
        this.formConfig.splice(i, 1);
        return;
      }
    }
  }

  onValueChange(form, updateFieldKeys: any) {
    // Widget should update only revision data
    if (form) {
      this.subject.next({ data: form, updateFieldKeys: updateFieldKeys}); // Using deboubce because autocomplete sends TWO events on every user selection
    }
  }
}
